import React from 'react'
import { Col } from 'react-bootstrap'
import PropTypes from 'prop-types'

import { NAV_MODULE_ICON } from '../../constants'
import { useAppContext } from '../../containers/App/Context'
import { getPalettePath } from '../../styles/constants'
import OutsideAlerter from '../OutsideAlerter'
import DeviceContentCategories from './DeviceContentCategories'
import DeviceCategoriesToggle from './DeviceCategoriesToggle'
import DevicePicker from './DevicePicker'
import DeviceLandingCategories from './DeviceLandingCategories'
import DeviceQueryToggle from './DeviceQueryToggle'
import messages from './messages'
import {
  StyledNavWithIdSelectorOverrides,
  StyledNavWrapperWithDropDown
} from './styled'
import { getVersionIcon } from '../../helpers/utils'

/**
 * Returns a presentable Menu with Nav items defined in this module.
 * Inline block, no overflow-y, with absolutely positioned categories.
 * Navheight overridable via STYLE configs and must account for selected
 * typeface, lineheight, svg size, and sibling span elements.
 * Enables a `more` button to expand horizontally challenged nav items.
 *
 * @module SubHeaderNavigationInlineBlock
 * @alias SubHeaderNavV2
 *
 * @see StyledNavWithIdSelectorOverrides
 * @see StyledNavWrapperWithDropDown
 *
 * @param {{
 *   currentDevice?: Device,
 *   headerLogoLink?: string,
 *   query: Object,
 *   language: string,
 *   onSvgLoad: (string) => HTMLOrSVGImageElement,
 *   modulesAvailable: ModulesAvailable,
 *   toggleMoreDropdown?: () => (value: React.SetStateAction<boolean>) => void,
 *   showMoreDropdown?: boolean,
 *   devices: Array<Device>,
 *   toggleDeviceModal: () => void,
 *   clearDevice: () => void,
 *   createUserEvent: ({ category: string, data: object }) => void,
 *   productNameSingular: string,
 *   productTranslation: string,
 *   children: React.ElementType
 * }}
 * @return {React.ReactNode}
 */
const SubHeaderNavigationInlineBlock = ({
  currentDevice,
  headerLogoLink,
  query,
  language,
  onSvgLoad,
  modulesAvailable,
  toggleMoreDropdown,
  showMoreDropdown,
  devices,
  toggleDeviceModal,
  clearDevice,
  productNameSingular,
  productTranslation,
  toggleSearchModal,
  externalNavEnabled
}) => {
  const { versionKey: version } = useAppContext()
  const palette = getPalettePath(version)
  const translated = messages[language]

  return (
    <>
      <StyledNavWithIdSelectorOverrides palette={palette}>
        <DeviceQueryToggle
          id="search"
          toggleSearchModal={toggleSearchModal}
          externalNavEnabled={externalNavEnabled}
          iconSrc={getVersionIcon(NAV_MODULE_ICON.search, version)}
        />
      </StyledNavWithIdSelectorOverrides>

      <Col xs={12} smHidden mdHidden lgHidden className="device-nav-col">
        <StyledNavWithIdSelectorOverrides palette={palette}>
          <ul id="express-navigation" className={currentDevice ? 'device-selected' : ''}>
            <li>
              <DevicePicker
              id="device-picker"
              devices={devices}
              toggleDeviceModal={toggleDeviceModal}
              currentDevice={currentDevice}
              clearDevice={clearDevice}
              removeIconSrc={getVersionIcon(NAV_MODULE_ICON.close, version)}
              selectIconSrc={getVersionIcon(NAV_MODULE_ICON.select, version)}
              selectTitle={
                productNameSingular
                  ? translated.select.replace(
                      productTranslation,
                      productNameSingular
                    )
                  : translated.select
              }
          />
            </li>
            <DeviceLandingCategories
              query={query}
              language={language}
              onSvgLoad={onSvgLoad}
              showHomeLink={headerLogoLink && true}
              showExpressLink={true}
              version={version}
            />
          </ul>
        </StyledNavWithIdSelectorOverrides>
      </Col>

      <Col sm={2} xsHidden className="device-nav-col">
        <StyledNavWithIdSelectorOverrides palette={palette}>
          <DevicePicker
            id="device-picker"
            devices={devices}
            toggleDeviceModal={toggleDeviceModal}
            currentDevice={currentDevice}
            clearDevice={clearDevice}
            removeIconSrc={getVersionIcon(NAV_MODULE_ICON.close, version)}
            selectIconSrc={getVersionIcon(NAV_MODULE_ICON.select, version)}
            selectTitle={
              productNameSingular
                ? translated.select.replace(
                    productTranslation,
                    productNameSingular
                  )
                : translated.select
            }
          />
        </StyledNavWithIdSelectorOverrides>
      </Col>

      <Col xs={12} smHidden mdHidden lgHidden className="device-nav-col">
        <StyledNavWithIdSelectorOverrides palette={palette}>
          <ul id="resources">
            <DeviceContentCategories
              currentDevice={currentDevice}
              query={query}
              language={language}
              onSvgLoad={onSvgLoad}
              modulesAvailable={modulesAvailable}
              showHomeLink={false}
              showExpressLink={false}
              version={version}
            />
          </ul>
        </StyledNavWithIdSelectorOverrides>
      </Col>

      <Col xsHidden sm={9} className="device-nav-col">
        {showMoreDropdown ? (
          <OutsideAlerter outsideAction={() => toggleMoreDropdown()}>
            <StyledNavWrapperWithDropDown
              id="menu"
              className="expanded"
              palette={palette}
            >
              <ul id="menu-closed">
                <DeviceContentCategories
                  currentDevice={currentDevice}
                  query={query}
                  language={language}
                  onSvgLoad={onSvgLoad}
                  modulesAvailable={modulesAvailable}
                  showHomeLink={headerLogoLink && true}
                  showExpressLink={true}
                  showMoreDropdown={showMoreDropdown}
                  toggleMoreDropdown={toggleMoreDropdown}
                  version={version}
                />
                <DeviceCategoriesToggle
                  toggleMoreDropdown={toggleMoreDropdown}
                  language={language}
                />
              </ul>
            </StyledNavWrapperWithDropDown>
          </OutsideAlerter>
        ) : (
          <StyledNavWrapperWithDropDown id="menu" palette={palette}>
            <ul id="menu-closed">
              <DeviceContentCategories
                currentDevice={currentDevice}
                query={query}
                language={language}
                onSvgLoad={onSvgLoad}
                modulesAvailable={modulesAvailable}
                showHomeLink={headerLogoLink && true}
                showExpressLink={true}
                showMoreDropdown={showMoreDropdown}
                toggleMoreDropdown={toggleMoreDropdown}
                version={version}
              />
              <DeviceCategoriesToggle
                toggleMoreDropdown={toggleMoreDropdown}
                language={language}
              />
            </ul>
          </StyledNavWrapperWithDropDown>
        )}
      </Col>
    </>
  )
}

SubHeaderNavigationInlineBlock.propTypes = {
  currentDevice: PropTypes.shape({
    customerID: PropTypes.string,
    id: PropTypes.number.isRequired,
    image_location: PropTypes.string.isRequired,
    manufacturer: PropTypes.string,
    name: PropTypes.string,
    name_pretty: PropTypes.string.isRequired,
    os: PropTypes.string,
    slug: PropTypes.string.isRequired,
    vanity_name: PropTypes.string
  }),
  headerLogoLink: PropTypes.string,
  query: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  onSvgLoad: PropTypes.func.isRequired,
  modulesAvailable: PropTypes.shape({
    tutorials: PropTypes.bool,
    faqs: PropTypes.bool,
    guides: PropTypes.bool,
    videos: PropTypes.bool
  }).isRequired,
  toggleMoreDropdown: PropTypes.func,
  showMoreDropdown: PropTypes.bool
}

export default SubHeaderNavigationInlineBlock



// WEBPACK FOOTER //
// ./src/components/SubHeader/SubHeaderNavigationInlineBlock.js